import React, { useState } from "react";
import { toast } from "sonner";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShoppingBag, Package, Plus, Trash2 } from "lucide-react";

const CUR = { scrap: "Scrap", hc: "HC", credits: "Credits" };

function Manager({ kind }) {
    const isKit = kind === "kits";
    const { community } = useAdmin();
    const { data: communities } = useFetch("/communities");
    const { data: items, reload } = useFetch(isKit ? "/kits" : "/shop");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        community_key: "CODERS_PLAYGROUND", name: "", description: "", price: 1000, currency: "scrap",
        category: "General", cooldown: "24h", contents: "",
    });
    if (!communities || !items) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k;
    const shown = community === "ALL" ? items : items.filter((i) => i.community_key === community);

    const save = async () => {
        if (!form.name) { toast.error("Name required"); return; }
        try {
            const payload = isKit
                ? { community_key: form.community_key, name: form.name, description: form.description, price: +form.price, currency: form.currency, cooldown: form.cooldown, contents: form.contents.split(",").map((s) => s.trim()).filter(Boolean) }
                : { community_key: form.community_key, name: form.name, description: form.description, price: +form.price, currency: form.currency, category: form.category, stock: -1 };
            await api.post(isKit ? "/admin/kits" : "/admin/shop", payload);
            toast.success(`${isKit ? "Kit" : "Item"} saved`, { description: form.name });
            setOpen(false); setForm({ ...form, name: "", description: "", contents: "" }); reload();
        } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || "" }); }
    };
    const del = async (id) => { await api.delete(`/admin/${isKit ? "kits" : "shop"}/${id}`); reload(); toast("Deleted"); };

    const Icon = isKit ? Package : ShoppingBag;
    return (
        <div data-testid={`admin-${kind}`}>
            <PageHeader title={isKit ? "Kits" : "Shop"} subtitle={isKit ? "Define player kits per community." : "Manage shop items per community."}
                action={<Button data-testid={`new-${kind}-btn`} onClick={() => setOpen(!open)} className="font-heading uppercase bg-burnt text-white hover:bg-burnt/90"><Plus size={15} className="mr-1" /> New</Button>} />
            {open && (
                <SectionCard title={`Create ${isKit ? "Kit" : "Item"}`} className="mb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Community">
                            <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                                <SelectTrigger data-testid={`${kind}-community`} className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Name"><Input data-testid={`${kind}-name`} value={form.name} onChange={(e) => upd("name", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Price"><Input data-testid={`${kind}-price`} type="number" value={form.price} onChange={(e) => upd("price", e.target.value)} className="bg-background border-border" /></Field>
                        <Field label="Currency">
                            <Select value={form.currency} onValueChange={(v) => upd("currency", v)}>
                                <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{Object.entries(CUR).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        {isKit
                            ? <Field label="Cooldown"><Input value={form.cooldown} onChange={(e) => upd("cooldown", e.target.value)} className="bg-background border-border" /></Field>
                            : <Field label="Category"><Input value={form.category} onChange={(e) => upd("category", e.target.value)} className="bg-background border-border" /></Field>}
                        <div className="md:col-span-2"><Field label="Description"><Input value={form.description} onChange={(e) => upd("description", e.target.value)} className="bg-background border-border" /></Field></div>
                        {isKit && <div className="md:col-span-2"><Field label="Contents (comma-separated)"><Input value={form.contents} onChange={(e) => upd("contents", e.target.value)} className="bg-background border-border" placeholder="AK, Meds, Armor" /></Field></div>}
                    </div>
                    <Button data-testid={`${kind}-save`} onClick={save} className="mt-3 font-heading uppercase bg-hazard text-black hover:bg-hazard/90">Save</Button>
                </SectionCard>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {shown.map((i) => (
                    <div key={i.id} data-testid={`${kind}-card-${i.id}`} className="tactical-card rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <Icon size={16} className="text-hazard" />
                            <button onClick={() => del(i.id)} className="text-danger/70 hover:text-danger"><Trash2 size={14} /></button>
                        </div>
                        <div className="font-heading text-lg font-bold uppercase text-foreground mt-2">{i.name}</div>
                        <div className="label-mono text-muted-foreground">{nameFor(i.community_key)}</div>
                        <p className="text-sm text-muted-foreground mt-1">{i.description}</p>
                        <div className="font-heading text-lg font-bold text-radioactive mt-2">{i.price === 0 ? "FREE" : `${i.price.toLocaleString()} ${CUR[i.currency]}`}</div>
                    </div>
                ))}
            </div>
            <Footer />
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="label-mono text-muted-foreground">{label}</label><div className="mt-1">{children}</div></div>;
}

export function Shop() { return <Manager kind="shop" />; }
export function Kits() { return <Manager kind="kits" />; }
